import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Collapse, Modal, Progress, Radio, Select, Space, Switch, Table, Tabs, Tag, Typography } from "antd";
import {
  AlertCircle, ArrowDownRight, ArrowUpRight, Calculator, CheckCircle, Clock,
  Download, Eye, Link2, RefreshCw, RotateCcw, Settings2, Tag as TagIcon, TrendingUp, Unlink2, XCircle,
} from "lucide-react";

const { Title, Text } = Typography;

type SyncStatus = "connected" | "disconnected" | "syncing" | "error";
type TxStatus = "matched" | "unmatched" | "ignored" | "pending";

interface Integration {
  id: "quickbooks" | "xero";
  name: string;
  tagline: string;
  color: string;
  status: SyncStatus;
  lastSync: string;
  orgName: string;
  syncedTransactions: number;
  unmatchedCount: number;
  syncInterval: string;
  autoSync: boolean;
}

interface Tx {
  id: string;
  date: string;
  description: string;
  category: string;
  amount: number;
  type: "income" | "expense";
  status: TxStatus;
  reference: string;
  note?: string;
}

interface SyncEvent {
  id: string;
  integration: "quickbooks" | "xero";
  timestamp: string;
  result: "success" | "warning" | "error";
  txCount: number;
  message: string;
}

const INITIAL_INTEGRATIONS: Integration[] = [
  { id: "quickbooks", name: "QuickBooks Online", tagline: "Intuit financial management", color: "#2CA01C", status: "connected", lastSync: "4 min ago", orgName: "Hairplane LLC", syncedTransactions: 284, unmatchedCount: 3, syncInterval: "Every 15 min", autoSync: true },
  { id: "xero", name: "Xero", tagline: "Cloud accounting platform", color: "#13B5EA", status: "disconnected", lastSync: "Never", orgName: "—", syncedTransactions: 0, unmatchedCount: 0, syncInterval: "Manual", autoSync: false },
];

const TRANSACTIONS: Tx[] = [
  { id: "t1", date: "Aug 8", description: "Haircut — Maya Chen", category: "Services", amount: 65, type: "income", status: "matched", reference: "QBO-8812" },
  { id: "t2", date: "Aug 8", description: "Color Treatment — Jordan", category: "Services", amount: 140, type: "income", status: "matched", reference: "QBO-8813" },
  { id: "t3", date: "Aug 8", description: "Product Sale — Olaplex Kit", category: "Product Sales", amount: 42, type: "income", status: "matched", reference: "QBO-8814" },
  { id: "t4", date: "Aug 8", description: "Walk-In Haircut — Sam", category: "Services", amount: 55, type: "income", status: "unmatched", reference: "—", note: "No matching entry in QBO" },
  { id: "t5", date: "Aug 7", description: "Balayage — Priya Patel", category: "Services", amount: 220, type: "income", status: "matched", reference: "QBO-8801" },
  { id: "t6", date: "Aug 7", description: "Supply Order — Salon Centric", category: "Supplies", amount: 312, type: "expense", status: "matched", reference: "QBO-8802" },
  { id: "t7", date: "Aug 7", description: "Gift Card Redemption", category: "Gift Cards", amount: 75, type: "income", status: "unmatched", reference: "—", note: "Gift card liability account not mapped" },
  { id: "t8", date: "Aug 6", description: "Booth Rental — Sofia Reyes", category: "Booth Rental", amount: 850, type: "income", status: "matched", reference: "QBO-8795" },
  { id: "t9", date: "Aug 6", description: "Tip Payout — Staff", category: "Tips", amount: 180, type: "expense", status: "pending", reference: "—", note: "Awaiting next sync" },
  { id: "t10", date: "Aug 6", description: "Beard Trim — Chris Rivera", category: "Services", amount: 35, type: "income", status: "matched", reference: "QBO-8796" },
  { id: "t11", date: "Aug 5", description: "Payroll Run — Aug 1–7", category: "Payroll", amount: 4200, type: "expense", status: "matched", reference: "QBO-8788" },
  { id: "t12", date: "Aug 5", description: "Highlights — Alex Kim", category: "Services", amount: 165, type: "income", status: "ignored", reference: "—", note: "Manually excluded" },
];

const SYNC_HISTORY: SyncEvent[] = [
  { id: "e1", integration: "quickbooks", timestamp: "Aug 8, 2026 · 2:44 PM", result: "success", txCount: 11, message: "11 transactions synced successfully" },
  { id: "e2", integration: "quickbooks", timestamp: "Aug 8, 2026 · 2:29 PM", result: "warning", txCount: 8, message: "8 synced · 1 unmatched gift card entry" },
  { id: "e3", integration: "quickbooks", timestamp: "Aug 8, 2026 · 2:14 PM", result: "success", txCount: 6, message: "6 transactions synced successfully" },
  { id: "e4", integration: "quickbooks", timestamp: "Aug 8, 2026 · 1:59 PM", result: "error", txCount: 0, message: "Auth token expired — re-authenticated" },
  { id: "e5", integration: "quickbooks", timestamp: "Aug 7, 2026 · 8:00 PM", result: "success", txCount: 22, message: "End-of-day sync: 22 transactions" },
  { id: "e6", integration: "quickbooks", timestamp: "Aug 6, 2026 · 8:00 PM", result: "success", txCount: 19, message: "End-of-day sync: 19 transactions" },
];

const CATEGORY_OPTIONS = ["Services", "Product Sales", "Gift Cards", "Tips", "Booth Rental", "Supplies", "Payroll", "Other"];
const ACCOUNT_OPTIONS = ["4000 · Sales Revenue", "4100 · Service Revenue", "4200 · Product Revenue", "2100 · Gift Card Liability", "6000 · Cost of Goods Sold", "6100 · Payroll Expense", "6200 · Supplies Expense"];

const STATUS_COLOR: Record<TxStatus, string> = { matched: "success", unmatched: "error", pending: "warning", ignored: "default" };
const fmt$ = (n: number) => `$${n.toLocaleString()}`;

function IntegrationCard({ intg, onToggle, onSync }: { intg: Integration; onToggle: () => void; onSync: () => void }) {
  const connected = intg.status === "connected";
  const syncing = intg.status === "syncing";
  return (
    <Card size="small" style={{ borderTop: `3px solid ${connected ? intg.color : "#e5e7eb"}`, opacity: connected || syncing ? 1 : 0.85 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Space>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: intg.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>
            {intg.id === "quickbooks" ? "QB" : "X"}
          </div>
          <div>
            <Text strong>{intg.name}</Text>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>{intg.tagline}</div>
          </div>
        </Space>
        <Tag color={connected ? "success" : syncing ? "processing" : intg.status === "error" ? "error" : "default"}>
          {connected ? "Connected" : syncing ? "Syncing…" : intg.status === "error" ? "Error" : "Not Connected"}
        </Tag>
      </div>

      {connected && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 16 }}>
            <div style={{ background: "#fafafa", borderRadius: 10, padding: 8, textAlign: "center" }}>
              <div style={{ fontWeight: 700 }}>{intg.syncedTransactions}</div>
              <div style={{ fontSize: 10, color: "#9ca3af" }}>Synced</div>
            </div>
            <div style={{ background: intg.unmatchedCount > 0 ? "#fff1f0" : "#fafafa", borderRadius: 10, padding: 8, textAlign: "center" }}>
              <div style={{ fontWeight: 700, color: intg.unmatchedCount > 0 ? "#cf1322" : undefined }}>{intg.unmatchedCount}</div>
              <div style={{ fontSize: 10, color: "#9ca3af" }}>Unmatched</div>
            </div>
            <div style={{ background: "#fafafa", borderRadius: 10, padding: 8, textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{intg.syncInterval}</div>
              <div style={{ fontSize: 10, color: "#9ca3af" }}>Interval</div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#8c8c8c", marginTop: 12 }}>
            <span><Link2 size={11} style={{ marginRight: 4 }} />Connected to <b>{intg.orgName}</b></span>
            <span><Clock size={10} style={{ marginRight: 4 }} />Last sync: {intg.lastSync}</span>
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        {connected ? (
          <>
            <Button size="small" style={{ flex: 1, background: intg.color, color: "#fff", border: "none" }} loading={syncing} icon={<RefreshCw size={12} />} onClick={onSync}>
              {syncing ? "Syncing…" : "Sync Now"}
            </Button>
            <Button size="small" danger icon={<Unlink2 size={12} />} onClick={onToggle}>Disconnect</Button>
          </>
        ) : (
          <Button size="small" block style={{ background: intg.color, color: "#fff", border: "none" }} icon={<Link2 size={12} />} onClick={onToggle}>
            Connect {intg.name}
          </Button>
        )}
      </div>
    </Card>
  );
}

export default function AdminAccountingPage() {
  const [integrations, setIntegrations] = useState<Integration[]>(INITIAL_INTEGRATIONS);
  const [connectModal, setConnectModal] = useState<Integration | null>(null);
  const [filter, setFilter] = useState<TxStatus | "all">("all");

  useEffect(() => {
    const id = setInterval(() => {
      setIntegrations((prev) => prev.map((i) =>
        i.id === "quickbooks" && i.status === "connected"
          ? { ...i, syncedTransactions: i.syncedTransactions + Math.floor(Math.random() * 3), lastSync: "just now" }
          : i
      ));
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  function toggleConnect(intg: Integration) {
    if (intg.status === "connected") {
      setIntegrations((prev) => prev.map((i) => (i.id === intg.id ? { ...i, status: "disconnected", lastSync: "Never", orgName: "—", syncedTransactions: 0, unmatchedCount: 0 } : i)));
    } else {
      setConnectModal(intg);
    }
  }

  function confirmConnect() {
    if (!connectModal) return;
    const id = connectModal.id;
    setIntegrations((prev) => prev.map((i) => (i.id === id ? { ...i, status: "syncing" } : i)));
    setConnectModal(null);
    setTimeout(() => {
      setIntegrations((prev) => prev.map((i) =>
        i.id === id ? { ...i, status: "connected", orgName: "Hairplane LLC", syncedTransactions: 0, unmatchedCount: 0, lastSync: "just now", autoSync: true } : i
      ));
    }, 2000);
  }

  function sync(intg: Integration) {
    setIntegrations((prev) => prev.map((i) => (i.id === intg.id ? { ...i, status: "syncing" } : i)));
    setTimeout(() => {
      setIntegrations((prev) => prev.map((i) => (i.id === intg.id ? { ...i, status: "connected", lastSync: "just now", syncedTransactions: i.syncedTransactions + 8 } : i)));
    }, 2200);
  }

  const matched = TRANSACTIONS.filter((t) => t.status === "matched").length;
  const unmatched = TRANSACTIONS.filter((t) => t.status === "unmatched").length;
  const pending = TRANSACTIONS.filter((t) => t.status === "pending").length;
  const income = TRANSACTIONS.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const expenses = TRANSACTIONS.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
  const matchPct = Math.round((matched / TRANSACTIONS.length) * 100);
  const connectedCount = integrations.filter((i) => i.status === "connected").length;

  const visibleTx = filter === "all" ? TRANSACTIONS : TRANSACTIONS.filter((t) => t.status === filter);

  const healthRows = [
    { label: "Services revenue", matched: 5, total: 6, note: "1 walk-in unmatched" },
    { label: "Product sales", matched: 1, total: 1, note: "Fully reconciled" },
    { label: "Gift cards", matched: 0, total: 1, note: "Liability account not mapped" },
    { label: "Tips & gratuity", matched: 0, total: 1, note: "Pending next sync" },
    { label: "Expenses", matched: 2, total: 2, note: "Fully reconciled" },
  ];

  const overviewTab = (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Net Revenue</Text><div style={{ fontSize: 20, fontWeight: 700, color: "#059669" }}>{fmt$(income - expenses)}</div><Text type="secondary" style={{ fontSize: 11 }}>{fmt$(income)} in · {fmt$(expenses)} out</Text></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Match Rate</Text><div style={{ fontSize: 20, fontWeight: 700, color: matchPct >= 90 ? "#059669" : "#d97706" }}>{matchPct}%</div><Text type="secondary" style={{ fontSize: 11 }}>{matched} of {TRANSACTIONS.length} matched</Text></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Unmatched</Text><div style={{ fontSize: 20, fontWeight: 700, color: unmatched > 0 ? "#cf1322" : "#059669" }}>{unmatched}</div><Text type="secondary" style={{ fontSize: 11 }}>Need review</Text></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Pending Sync</Text><div style={{ fontSize: 20, fontWeight: 700, color: "#d97706" }}>{pending}</div><Text type="secondary" style={{ fontSize: 11 }}>Queued</Text></Card>
      </div>

      <Title level={5}><Link2 size={15} style={{ marginRight: 6, verticalAlign: -2 }} />Platform Connections</Title>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {integrations.map((intg) => (
          <IntegrationCard key={intg.id} intg={intg} onToggle={() => toggleConnect(intg)} onSync={() => sync(intg)} />
        ))}
      </div>

      <Card size="small" title={<span><CheckCircle size={14} color="#10b981" style={{ marginRight: 6 }} />Reconciliation Health</span>}>
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          {healthRows.map((r) => {
            const pct = Math.round((r.matched / r.total) * 100);
            return (
              <div key={r.label}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <Text strong style={{ fontSize: 12 }}>{r.label}</Text>
                  <Text style={{ fontSize: 12, color: pct === 100 ? "#059669" : pct === 0 ? "#cf1322" : "#d97706" }}>{pct}% · {r.note}</Text>
                </div>
                <Progress percent={pct} showInfo={false} strokeColor={pct === 100 ? "#10b981" : pct === 0 ? "#f87171" : "#fbbf24"} size="small" />
              </div>
            );
          })}
        </Space>
      </Card>
    </div>
  );

  const reconciliationTab = (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {(["all", "matched", "unmatched", "pending", "ignored"] as const).map((f) => (
          <Button key={f} size="small" type={filter === f ? "primary" : "default"} onClick={() => setFilter(f)} style={{ textTransform: "capitalize" }}>
            {f === "all" ? `All (${TRANSACTIONS.length})` : `${f} (${TRANSACTIONS.filter((t) => t.status === f).length})`}
          </Button>
        ))}
        <Button size="small" type="text" icon={<Download size={12} />} style={{ marginLeft: "auto" }}>Export CSV</Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        dataSource={visibleTx}
        pagination={false}
        columns={[
          { title: "Date", dataIndex: "date", width: 70 },
          {
            title: "Description", dataIndex: "description",
            render: (v: string, tx: Tx) => (
              <div>
                <div style={{ fontWeight: 500 }}>{v}</div>
                {tx.note && <div style={{ fontSize: 11, color: "#d97706", display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}><AlertCircle size={10} />{tx.note}</div>}
              </div>
            ),
          },
          { title: "Category", dataIndex: "category", render: (v: string) => <Tag icon={<TagIcon size={9} />}>{v}</Tag> },
          {
            title: "Amount", dataIndex: "amount",
            render: (v: number, tx: Tx) => (
              <span style={{ color: tx.type === "income" ? "#059669" : "#ef4444", fontWeight: 600, display: "flex", alignItems: "center", gap: 2 }}>
                {tx.type === "income" ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{fmt$(v)}
              </span>
            ),
          },
          { title: "Status", dataIndex: "status", render: (v: TxStatus) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
          { title: "Reference", dataIndex: "reference", render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
          { title: "", key: "view", width: 30, render: () => <Eye size={14} color="#d9d9d9" /> },
        ]}
      />
    </div>
  );

  const historyTab = (
    <Card size="small">
      {SYNC_HISTORY.map((ev, i) => (
        <div key={ev.id} style={{ display: "flex", gap: 10, padding: "12px 0", borderTop: i > 0 ? "1px solid #f0f0f0" : undefined }}>
          {ev.result === "success" ? <CheckCircle size={15} color="#10b981" /> : ev.result === "warning" ? <AlertCircle size={15} color="#f59e0b" /> : <XCircle size={15} color="#ef4444" />}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Text style={{ fontSize: 13 }}>{ev.message}</Text>
              <Tag color={ev.integration === "quickbooks" ? "green" : "blue"} style={{ fontSize: 10 }}>{ev.integration === "quickbooks" ? "QuickBooks" : "Xero"}</Tag>
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              <Clock size={9} style={{ marginRight: 4 }} />{ev.timestamp}
              {ev.txCount > 0 && <span style={{ marginLeft: 8, fontWeight: 600, color: "#595959" }}>{ev.txCount} transactions</span>}
            </div>
          </div>
        </div>
      ))}
    </Card>
  );

  const settingsTab = (
    <Collapse
      defaultActiveKey={["mapping"]}
      items={[
        {
          key: "mapping",
          label: <span><TagIcon size={13} style={{ marginRight: 6 }} />Account & Category Mapping</span>,
          children: (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Map Hairplane categories to your chart of accounts.</Text>
              {CATEGORY_OPTIONS.map((cat) => (
                <div key={cat} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Text style={{ width: 120, fontSize: 13 }}>{cat}</Text>
                  <Select style={{ flex: 1 }} placeholder="— Select account —" options={ACCOUNT_OPTIONS.map((a) => ({ value: a, label: a }))} />
                </div>
              ))}
            </Space>
          ),
        },
        {
          key: "sync",
          label: <span><RefreshCw size={13} style={{ marginRight: 6 }} />Sync Schedule & Triggers</span>,
          children: (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><Text strong style={{ fontSize: 13 }}>Auto-sync</Text><div style={{ fontSize: 11, color: "#9ca3af" }}>Automatically push transactions on each checkout</div></div>
                <Switch defaultChecked />
              </div>
              <div>
                <Text strong style={{ fontSize: 13, display: "block", marginBottom: 6 }}>Sync frequency</Text>
                <Select style={{ width: "100%" }} defaultValue="15" options={[{ value: "15", label: "Every 15 minutes" }, { value: "30", label: "Every 30 minutes" }, { value: "60", label: "Every hour" }, { value: "eod", label: "End of day" }, { value: "manual", label: "Manual only" }]} />
              </div>
              <div>
                <Text strong style={{ fontSize: 13, display: "block", marginBottom: 6 }}>Sync direction</Text>
                <Radio.Group defaultValue="oneway">
                  <Radio value="oneway">One-way (Hairplane → QB)</Radio>
                  <Radio value="twoway">Two-way sync</Radio>
                </Radio.Group>
              </div>
            </Space>
          ),
        },
        {
          key: "tax",
          label: <span>Tax Configuration</span>,
          children: (
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><Text style={{ fontSize: 13 }}>Include sales tax in sync</Text><Switch defaultChecked /></div>
              <div>
                <Text strong style={{ fontSize: 13, display: "block", marginBottom: 6 }}>Default tax code</Text>
                <Select style={{ width: "100%" }} defaultValue="std" options={[{ value: "std", label: "TAX — Standard rate (8.875%)" }, { value: "exempt", label: "EXEMPT — Tax exempt" }, { value: "oos", label: "OUT_OF_SCOPE" }]} />
              </div>
              <Alert type="info" showIcon message="Tax codes are mapped per service type. Overrides apply to individual line items." />
            </Space>
          ),
        },
        {
          key: "notifications",
          label: <span>Alerts & Notifications</span>,
          children: (
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              {[
                { label: "Email on sync failure", on: true },
                { label: "Daily reconciliation summary", on: true },
                { label: "Alert on unmatched transactions", on: true },
                { label: "Weekly financial digest", on: false },
              ].map((item) => (
                <div key={item.label} style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: 13 }}>{item.label}</Text>
                  <Switch defaultChecked={item.on} />
                </div>
              ))}
            </Space>
          ),
        },
      ]}
    />
  );

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Calculator size={16} color="#fff" />
            </div>
            <Title level={3} style={{ margin: 0 }}>Accounting Integration</Title>
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {connectedCount > 0 ? `${connectedCount} platform${connectedCount > 1 ? "s" : ""} connected · auto-reconciliation active` : "Connect your accounting platform to begin reconciliation"}
          </Text>
        </div>
        {connectedCount > 0 && <Badge status="processing" text="Live reconciliation active" />}
      </div>

      <Alert
        style={{ marginBottom: 16 }}
        type="warning"
        showIcon
        message="Demo preview — not connected to a real QuickBooks/Xero account"
        description="This screen is fully interactive so you can try the flow, but nothing here talks to a real accounting platform yet. Real integration needs a QuickBooks/Xero developer account and OAuth app registration."
      />

      <Tabs
        items={[
          { key: "overview", label: <span><TrendingUp size={13} style={{ marginRight: 5 }} />Overview</span>, children: overviewTab },
          { key: "reconciliation", label: "Reconciliation", children: reconciliationTab },
          { key: "history", label: <span><RotateCcw size={13} style={{ marginRight: 5 }} />Sync History</span>, children: historyTab },
          { key: "settings", label: <span><Settings2 size={13} style={{ marginRight: 5 }} />Settings</span>, children: settingsTab },
        ]}
      />

      <Modal
        title={connectModal ? `Connect ${connectModal.name}` : ""}
        open={!!connectModal}
        onCancel={() => setConnectModal(null)}
        onOk={confirmConnect}
        okText="Authorize & Connect"
        okButtonProps={{ style: connectModal ? { background: connectModal.color, borderColor: connectModal.color } : undefined }}
      >
        {connectModal && (
          <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>You'll be redirected to authorize access (simulated for this demo)</Text>
            <Card size="small" style={{ background: "#fafafa" }}>
              <Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c" }}>Hairplane will access:</Text>
              <div style={{ marginTop: 8 }}>
                {["Read & write chart of accounts", "Read & write transactions", "Read tax codes", "Read company info"].map((item) => (
                  <div key={item} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginTop: 4 }}>
                    <CheckCircle size={12} color="#10b981" />{item}
                  </div>
                ))}
              </div>
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
}
